/** @format */

import React from 'react'
import { _$, Icon, Option, Select } from '@king-design/react'
import styled from 'styled-components'
import { FilterMtchProps } from '../typescript'
import { MatchMode } from './../../SessionAnalysis/typescript'
import variable from 'src/static/styles/var'
import { emptySpanValue } from '../constant'
import { getTraceSpanAttr, getFieldOptions } from 'src/api/ApiAIObservability'
import { useAsync } from 'react-use'

const TEXT_MATCH_OPTS: ReadonlyArray<{ value: MatchMode; label: string }> = [
  { value: 'in', label: _$('包含') },
  { value: 'not_in', label: _$('不包含') }
]

interface SpanItemProps {
  Key: string
  Op: MatchMode
  Values: string[]
  index: number
  patchRow: (index: number, patch: Partial<FilterMtchProps>) => void
  removeRow: (index: number) => void
  attrOptions: string[]
  startTime: number
  endTime: number
}

const SpanItem: React.FC<SpanItemProps> = ({
  Key,
  Op,
  Values,
  index,
  patchRow,
  removeRow,
  attrOptions,
  startTime,
  endTime
}) => {
  const { value: valueOptions } = useAsync(async () => {
    if (Key) {
      const result = await getFieldOptions({
        StartTime: startTime,
        EndTime: endTime,
        FieldName: Key
      })
      return result || []
    } else {
      return []
    }
    return []
  }, [startTime, endTime, Key])
  return (
    <li className="span-attr-filter-row" key={index}>
      <Select
        width={200}
        clearable
        className="span_attr_key"
        placeholder={_$('属性名')}
        value={Key || undefined}
        onChangeValue={v => patchRow(index, { Key: String(v ?? '') })}
      >
        {attrOptions?.map(name => (
          <Option key={name} value={name}>
            {name}
          </Option>
        ))}
      </Select>
      <Select
        width={88}
        clearable
        className="span_attr_op"
        placeholder={_$('操作符')}
        value={Op ?? 'in'}
        onChangeValue={v => patchRow(index, { Op: v as MatchMode })}
      >
        {TEXT_MATCH_OPTS.map(o => (
          <Option key={o.value} value={o.value}>
            {_$(o.label)}
          </Option>
        ))}
      </Select>
      <Select
        width={400}
        clearable
        multiple
        filterable
        nowrap
        className="span_attr_value"
        placeholder={_$('属性值')}
        value={Values || undefined}
        onChangeValue={v => patchRow(index, { Values: v as string[] })}
      >
        {valueOptions?.map(o => (
          <Option key={o} value={o}>
            {o}
          </Option>
        ))}
      </Select>
      <Icon
        className="span-attr-filter-remove ion-trash-a"
        size={16}
        aria-label={_$('删除条件')}
        onClick={() => removeRow(index)}
      />
    </li>
  )
}

export interface SpanSearchProps {
  className?: string
  value: FilterMtchProps[]
  onChange: (next: FilterMtchProps[]) => void
  startTime: number
  endTime: number
}
const SpanSearch: React.FC<SpanSearchProps> = ({
  className,
  value,
  onChange,
  startTime,
  endTime
}) => {
  const addRow = () => {
    onChange([...value, emptySpanValue()])
  }
  const patchRow = (index: number, patch: Partial<FilterMtchProps>) => {
    const base = value.map((r, i) => (i === index ? { ...r, ...patch } : r))
    onChange(base)
  }

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const { value: attrOptions } = useAsync(async () => {
    if (startTime && endTime) {
      const result = await getTraceSpanAttr({
        StartTime: startTime,
        EndTime: endTime
      })
      return result?.filter(item => item.Category === 'span_attr')?.map(item => item.Name) || []
    }
    return []
  }, [startTime, endTime])

  return (
    <div className={className}>
      <div className="span-attr-filter">
        <div className="span-attr-filter-title">{_$('Span 属性筛选')}：</div>
        <ul className="span-attr-filter-list">
          {value.map((row, index) => (
            <SpanItem
              key={index}
              {...row}
              index={index}
              patchRow={patchRow}
              removeRow={removeRow}
              attrOptions={attrOptions}
              startTime={startTime}
              endTime={endTime}
            />
          ))}
        </ul>
        <Icon
          className="span-attr-filter-add k-icon-add-bold"
          aria-label={_$('新建条件')}
          onClick={addRow}
        />
      </div>
    </div>
  )
}

export default styled(SpanSearch)`
  width: 100%;
  margin-top: 8px;

  .span-attr-filter {
    width: 100%;
    display: flex;
    align-items: flex-start;
    &-title {
      line-height: 32px;
      color: var(--k-text-color, rgba(0, 0, 0, 0.85));
      margin-right: 8px;
    }

    &-list {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    &-row {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      min-width: 0;

      .span_attr_key.k-select {
        min-width: 0;
        border-radius: 4px 0 0 4px;
      }

      .span_attr_op.k-select {
        flex-shrink: 0;
        border-radius: 0;
        border-right: none;
        border-left: none;
      }

      .span_attr_value.k-select {
        min-width: 0;
        border-radius: 0 4px 4px 0;
        margin-right: 8px;
      }
    }

    &-remove {
      flex-shrink: 0;
      align-self: center;
      color: var(--k-text-secondary-color, rgba(0, 0, 0, 0.45));
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: ${variable.primaryColor};
      }
    }

    &-add {
      display: block;
      margin-bottom: 8px;
      margin-left: 8px;
      height: 32px;
      line-height: 32px;
      color: var(--k-text-secondary-color, rgba(0, 0, 0, 0.45));
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: ${variable.primaryColor};
      }
    }
  }
`
